import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Paper,
  Alert,
} from '@mui/material';
import { CheckCircle, Error, Info } from '@mui/icons-material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import BaseModal from './BaseModal';

export interface DateFormatRule {
  inputFormat: string;
  outputFormat: string; // Always "YYYY-MM-DD" (Illio format)
  exampleInput?: string;
  exampleOutput?: string;
}

interface DateFormatMatchingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (rule: DateFormatRule) => void;
  initialRule?: DateFormatRule;
  fieldName: string;
}

const DateFormatMatchingModal: React.FC<DateFormatMatchingModalProps> = ({
  open,
  onClose,
  onSave,
  initialRule,
  fieldName,
}) => {
  const [inputFormat, setInputFormat] = useState('MM/DD/YYYY');
  const [exampleInput, setExampleInput] = useState('');

  // Output format is always YYYY-MM-DD (Illio required format)
  const outputFormat = 'YYYY-MM-DD';

  useEffect(() => {
    if (initialRule) {
      setInputFormat(initialRule.inputFormat);
      setExampleInput(initialRule.exampleInput || '');
    } else {
      setInputFormat('MM/DD/YYYY');
      setExampleInput('');
    }
  }, [initialRule]);

  const inputFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '12/25/2023' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '25/12/2023' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2023-12-25' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY', example: '25-12-2023' },
    { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY', example: '12-25-2023' },
    { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY', example: '25.12.2023' },
    { value: 'MM.DD.YYYY', label: 'MM.DD.YYYY', example: '12.25.2023' },
    { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD', example: '2023/12/25' },
  ];

  const parseDate = (dateString: string, format: string) => {
    if (!dateString) return null;

    const cleanDate = dateString.trim();
    let day: string, month: string, year: string;

    try {
      switch (format) {
        case 'MM/DD/YYYY':
          [month, day, year] = cleanDate.split('/');
          break;
        case 'DD/MM/YYYY':
          [day, month, year] = cleanDate.split('/');
          break;
        case 'YYYY-MM-DD':
          [year, month, day] = cleanDate.split('-');
          break;
        case 'DD-MM-YYYY':
          [day, month, year] = cleanDate.split('-');
          break;
        case 'MM-DD-YYYY':
          [month, day, year] = cleanDate.split('-');
          break;
        case 'DD.MM.YYYY':
          [day, month, year] = cleanDate.split('.');
          break;
        case 'MM.DD.YYYY':
          [month, day, year] = cleanDate.split('.');
          break;
        case 'YYYY/MM/DD':
          [year, month, day] = cleanDate.split('/');
          break;
        default:
          return null;
      }

      if (!day || !month || !year) return null;

      // Basic validation
      const dayNum = parseInt(day, 10);
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);

      if (
        dayNum < 1 ||
        dayNum > 31 ||
        monthNum < 1 ||
        monthNum > 12 ||
        yearNum < 1900 ||
        yearNum > 2100
      ) {
        return null;
      }

      return {
        day: day.padStart(2, '0'),
        month: month.padStart(2, '0'),
        year: year,
        isValid: true,
      };
    } catch {
      return null;
    }
  };

  const generateExampleOutput = () => {
    if (!exampleInput) return { output: '', isValid: null };

    const parsed = parseDate(exampleInput, inputFormat);
    if (!parsed) {
      return { output: 'Invalid date format', isValid: false };
    }

    return {
      output: `${parsed.year}-${parsed.month}-${parsed.day}`,
      isValid: true,
    };
  };

  const handleSave = () => {
    const exampleResult = generateExampleOutput();
    const rule: DateFormatRule = {
      inputFormat: inputFormat,
      outputFormat: outputFormat,
      exampleInput,
      exampleOutput: exampleResult.output,
    };

    onSave(rule);
    onClose();
  };

  const exampleResult = generateExampleOutput();
  const selectedFormatOption = inputFormatOptions.find(
    opt => opt.value === inputFormat
  );

  return (
    <BaseModal
      dataTestId="date-format-matching-modal"
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={`Date Format Configuration - ${fieldName}`}
      icon={<CalendarIcon />}
      submitText="Save Configuration"
      submitDisabled={!inputFormat}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Why configure date formats?
          </Typography>
          <Typography variant="body2">
            Your Excel file may have dates in different formats (like 12/25/2023
            or 25-12-2023). This tool converts them to the standard YYYY-MM-DD
            format required by Illio.
          </Typography>
        </Alert>

        <Paper variant="text" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Info color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Select Your Excel Date Format
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose the format that matches how dates appear in your Excel file.
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Excel Date Format</InputLabel>
            <Select
              value={inputFormat}
              label="Excel Date Format"
              onChange={e => setInputFormat(e.target.value)}
            >
              {inputFormatOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: option.value === inputFormat ? 600 : 400,
                        }}
                      >
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Example: {option.example}
                      </Typography>
                    </Box>
                    {option.value === inputFormat && (
                      <CheckCircle color="primary" fontSize="small" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Paper
          variant="text"
          sx={{ p: 3, borderRadius: 2, bgcolor: 'primary.50' }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Output Format
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            All dates will be automatically converted to:{' '}
            <strong>YYYY-MM-DD</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Example: 2023-12-25
          </Typography>
        </Paper>

        <Paper variant="text" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Test Conversion
          </Typography>

          <TextField
            label="Test with sample date"
            value={exampleInput}
            onChange={e => setExampleInput(e.target.value)}
            fullWidth
            placeholder={`Enter a date in ${inputFormat} format (e.g., ${selectedFormatOption?.example})`}
            error={exampleInput && exampleResult.isValid === false}
            helperText={
              !exampleInput
                ? 'Enter a sample date to test the conversion'
                : exampleResult.isValid === true
                  ? `✓ Will be converted to: ${exampleResult.output}`
                  : `✗ ${exampleResult.output}`
            }
            InputProps={{
              endAdornment:
                exampleInput &&
                (exampleResult.isValid === true ? (
                  <CheckCircle color="success" fontSize="small" />
                ) : exampleResult.isValid === false ? (
                  <Error color="error" fontSize="small" />
                ) : null),
            }}
          />

          {exampleInput && exampleResult.isValid === true && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'success.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 600 }}
              >
                Conversion Preview:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Input:</strong> {exampleInput} ({inputFormat})
              </Typography>
              <Typography variant="body2">
                <strong>Output:</strong> {exampleResult.output} (YYYY-MM-DD)
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    </BaseModal>
  );
};

export default DateFormatMatchingModal;
